import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import SettingsIcon from "@mui/icons-material/Settings";
import {
  Alert,
  Breadcrumbs,
  Chip,
  IconButton,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";
import { useState } from "react";
import { Link } from "react-router-dom";

import { useAuth } from "@/providers";
import { S3Icon } from "@/Settings/Integrations/IntegrationIcon";
import { PageLayout } from "@/shared/components/PageLayout";
import { FormattedDateTime } from "@/shared/datetime";
import { useS3BucketIntegrations } from "@/shared/domain/integrations/hooks/useS3BucketIntegrations";
import { useTestS3Connection } from "@/shared/domain/integrations/hooks/useTestS3Connection";
import { Goto } from "@/shared/navigation/Goto";

import { HealthCheckDialog } from "./HealthCheckDialog";
import { formatHealthToken, healthStatusColor } from "./s3HealthStatus";
import styles from "./S3Integration.module.css";

const CONTACT_URL = "https://www.roboto.ai/contact";
const COLUMN_COUNT = 6;

export function S3Integration() {
  const { currentOrganization } = useAuth();
  const orgId = currentOrganization?.org_id;

  const {
    data: buckets,
    isLoading,
    isError,
    error,
  } = useS3BucketIntegrations(orgId);

  const testConnection = useTestS3Connection();
  const [selectedBucketName, setSelectedBucketName] = useState<string | null>(
    null,
  );
  const selectedBucket = buckets?.find(
    (bucket) => bucket.bucket_name === selectedBucketName,
  );

  const closeDialog = () => {
    setSelectedBucketName(null);
    testConnection.reset();
  };

  const runHealthCheck = () => {
    if (selectedBucketName && orgId) {
      testConnection.mutate({ bucketName: selectedBucketName, orgId });
    }
  };

  return (
    <PageLayout
      title="Roboto - AWS S3 Integration"
      headerContentLeft={
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          aria-label="breadcrumb"
          className={styles.breadcrumbs}
        >
          <Link to={Goto.route.Settings.Root} className={styles.breadcrumbLink}>
            <SettingsIcon fontSize="small" />
            <span>Settings</span>
          </Link>
          <Link
            to={Goto.route.Settings.Integrations}
            className={styles.breadcrumbLink}
          >
            <span>Integrations</span>
          </Link>
          <span className={styles.breadcrumbCurrent}>AWS S3</span>
        </Breadcrumbs>
      }
    >
      <div className={styles.headerContainer}>
        <div className={styles.headerWithIcon}>
          <S3Icon size={32} />
          AWS S3 Integration
        </div>
      </div>

      <p className={styles.description}>
        View your connected S3 buckets. When your organization uses
        bring-your-own-bucket (BYOB) storage, data stored in your own buckets is
        managed directly by you and is not reflected in Roboto usage metrics.
      </p>

      <p className={styles.contactPrompt}>
        Need to connect a new bucket or modify an existing integration?{" "}
        <a
          href={CONTACT_URL}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.contactLink}
        >
          Contact us
        </a>{" "}
        to get started.
      </p>

      {isError && (
        <Alert severity="error" className={styles.tableContainer}>
          Failed to load S3 bucket integrations
          {error?.message ? `: ${error.message}` : "."}
        </Alert>
      )}

      {!isError && (
        <TableContainer
          component={Paper}
          variant="outlined"
          className={styles.tableContainer}
        >
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Bucket Name</TableCell>
                <TableCell>AWS Region</TableCell>
                <TableCell>Access</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Last Updated</TableCell>
                <TableCell>Connected</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading &&
                Array.from({ length: 3 }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: COLUMN_COUNT }).map((__, j) => (
                      <TableCell key={j}>
                        <Skeleton variant="text" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))}

              {!isLoading && buckets && buckets.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={COLUMN_COUNT}
                    className={styles.emptyState}
                  >
                    <span className={styles.emptyStateText}>
                      No S3 buckets are connected to this organization.
                    </span>
                  </TableCell>
                </TableRow>
              )}

              {!isLoading &&
                buckets?.map((bucket) => (
                  <TableRow key={bucket.bucket_name}>
                    <TableCell>{bucket.bucket_name}</TableCell>
                    <TableCell>{bucket.aws_region}</TableCell>
                    <TableCell>
                      {bucket.readonly ? "Read Only" : "Read / Write"}
                    </TableCell>
                    <TableCell>
                      <div className={styles.statusCell}>
                        <Chip
                          label={formatHealthToken(bucket.status)}
                          size="small"
                          variant="outlined"
                          color={healthStatusColor(bucket.status)}
                        />
                        <Tooltip title="Health check details">
                          <IconButton
                            size="small"
                            aria-label={`Health check details for ${bucket.bucket_name}`}
                            onClick={() =>
                              setSelectedBucketName(bucket.bucket_name)
                            }
                          >
                            <InfoOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </div>
                    </TableCell>
                    <TableCell>
                      {bucket.status_last_updated ? (
                        <FormattedDateTime value={bucket.status_last_updated} />
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    <TableCell>
                      <FormattedDateTime value={bucket.created} time="none" />
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <HealthCheckDialog
        open={selectedBucket !== undefined}
        onClose={closeDialog}
        bucket={selectedBucket}
        onRunCheck={runHealthCheck}
        isRunning={testConnection.isPending}
        runError={testConnection.error}
      />
    </PageLayout>
  );
}
